import { useState, useEffect } from "react";
import {
  Form,
  Button,
  Spinner,
  Row,
  Col,
  Card,
  InputGroup,
  Alert,
} from "react-bootstrap";
import { FiEye, FiEyeOff } from "react-icons/fi";
import {
  useCreateUserMutation,
  useUpdateUserMutation,
} from "../../../../../api/fleet-management/users/users.hooks";

const FleetUserForm = ({
  onCancel,
  onSave,
  user = null,
  mode = "create", // 'create', 'edit', 'view'
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    contactNumber: "",
    role: "manager",
    isActive: true,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const {
    mutateAsync: createFleetUser,
    isPending: isCreating,
    error: createError,
  } = useCreateUserMutation();
  const {
    mutateAsync: updateFleetUser,
    isPending: isUpdating,
    error: updateError,
  } = useUpdateUserMutation();

  const isLoading = isCreating || isUpdating;
  const error = createError || updateError;

  useEffect(() => {
    if (user && (mode === "edit" || mode === "view")) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        password: "",
        contactNumber: user.contactNumber || "",
        role: (user.role || "manager").toLowerCase(),
        isActive: user.isActive !== undefined ? user.isActive : true,
      });
    } else {
      setFormData({
        name: "",
        email: "",
        password: "",
        contactNumber: "",
        role: "manager",
        isActive: true,
      });
    }
    setValidationErrors({});
  }, [user, mode]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }

    if (mode === "create" && !formData.password.trim()) {
      errors.password = "Password is required";
    }

    if (
      (mode === "create" || formData.password.trim()) &&
      formData.password.trim().length < 6
    ) {
      errors.password = "Password must be at least 6 characters";
    }

    if (!formData.role) {
      errors.role = "Role is required";
    }

    if (formData.contactNumber && !/^\+?[0-9]{7,15}$/.test(formData.contactNumber)) {
      errors.contactNumber = "Contact number should be 7-15 digits";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (mode === "view") {
      onCancel();
      return;
    }

    if (!validateForm()) return;

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        role: formData.role,
        isActive: formData.isActive,
      };

      if (formData.contactNumber.trim()) {
        payload.contactNumber = formData.contactNumber.trim();
      }

      if (mode === "create" || (mode === "edit" && formData.password.trim())) {
        payload.password = formData.password;
      }

      if (mode === "create") {
        await createFleetUser(payload);
      } else if (mode === "edit" && (user?._id || user?.id)) {
        const userId = user._id || user.id;
        await updateFleetUser({ userId, userData: payload });
      }

      onSave && onSave(payload);
    } catch (err) {
      console.error("Fleet user mutation error:", err);
    }
  };

  const getFormTitle = () => {
    switch (mode) {
      case "create":
        return "Add New Fleet User";
      case "edit":
        return "Edit Fleet User";
      case "view":
        return "View Fleet User Details";
      default:
        return "Fleet User";
    }
  };

  const isReadOnly = mode === "view";

  return (
    <Card className="p-4">
      <div className="d-flex align-items-center mb-4">
        <Button variant="link" className="p-0 me-3" onClick={onCancel}>
          ← Back
        </Button>
        <h4 className="m-0">{getFormTitle()}</h4>
      </div>

      {error && (
        <Alert variant="danger" dismissible>
          {typeof error === "string"
            ? error
            : error?.response?.data?.message ||
              error?.message ||
              "An error occurred"}
        </Alert>
      )}

      <Form>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                disabled={isReadOnly}
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Email {!isReadOnly && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                isInvalid={!!validationErrors.email}
                disabled={isReadOnly}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.email}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Password{" "}
                {!isReadOnly && mode === "create" && (
                  <span className="text-danger">*</span>
                )}
                {mode === "edit" && (
                  <small className="text-muted"> (Leave blank to keep current)</small>
                )}
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder={mode === "edit" ? "Enter new password" : "Enter password"}
                  value={formData.password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  isInvalid={!!validationErrors.password}
                  disabled={isReadOnly}
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isReadOnly}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.password}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Contact Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter contact number"
                value={formData.contactNumber}
                onChange={(e) => handleInputChange("contactNumber", e.target.value)}
                isInvalid={!!validationErrors.contactNumber}
                disabled={isReadOnly}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.contactNumber}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Role {!isReadOnly && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Select
                value={formData.role}
                onChange={(e) => handleInputChange("role", e.target.value)}
                isInvalid={!!validationErrors.role}
                disabled={isReadOnly}
              >
                <option value="">Select Role</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {validationErrors.role}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <div className="mt-2">
                <Form.Check
                  type="switch"
                  id="fleet_is_active_switch"
                  label={formData.isActive ? "Active" : "Inactive"}
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange("isActive", e.target.checked)}
                  disabled={isReadOnly}
                />
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Module</Form.Label>
              <Form.Control type="text" value="Fleet Management" disabled />
              <Form.Text className="text-muted">
                This form is for Fleet Management users only
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            {mode === "view" ? "Close" : "Cancel"}
          </Button>
          {mode !== "view" && (
            <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {mode === "create" ? "Adding..." : "Saving..."}
                </>
              ) : mode === "create" ? (
                "Add User"
              ) : (
                "Save Changes"
              )}
            </Button>
          )}
        </div>
      </Form>
    </Card>
  );
};

export default FleetUserForm;
